import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAnonymizeLead } from "@/hooks/use-leads";
import { useListLeads } from "@/hooks/use-list-leads";
import { LEAD_STATUS_LABELS } from "@/lib/lead-stats-labels";

interface ListLeadsDialogProps {
  listId: string | null;
  listName: string;
  onOpenChange: (open: boolean) => void;
}

/** Leads i en liste, med «Slett person» (anonymisering) for admin. */
export default function ListLeadsDialog({ listId, listName, onOpenChange }: ListLeadsDialogProps) {
  const [search, setSearch] = useState("");
  const { data, isLoading } = useListLeads(listId ?? undefined, search);
  const anonymize = useAnonymizeLead();

  const onAnonymize = (id: string, name: string) => {
    if (!confirm(`Slette ${name}? Selskapet og kontaktpersonene anonymiseres og kan ikke hentes tilbake.`)) return;
    anonymize.mutate(id);
  };

  return (
    <Dialog open={!!listId} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{listName}</DialogTitle>
          <DialogDescription>Viser inntil 200 leads. Bruk søk for å finne et bestemt selskap.</DialogDescription>
        </DialogHeader>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søk på navn" aria-label="Søk i leads" />
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Laster…</p>
        ) : (data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen leads funnet i denne listen.</p>
        ) : (
          <ul className="divide-y divide-border">
            {(data ?? []).map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{l.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {LEAD_STATUS_LABELS[l.status] ?? l.status} · {l.attempts_count} forsøk
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={anonymize.isPending}
                  aria-label={`Slett person: ${l.name}`}
                  onClick={() => onAnonymize(l.id, l.name)}
                >
                  Slett person
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
